(function () {
  'use strict';

  /*
   * GRANDEVERSE AI - NOVA 3.3 (FULL)
   *
   * FIXES (Frontend hardening):
   * ✅ Variant locking: purchase card + Buy Now always uses CURRENTLY selected variant (URL/form/meta)
   * ✅ "Buy Now" added (add to cart -> redirect to checkout)
   * ✅ Recommendations show ONLY if the user explicitly asks for them (similar/alternative/suggest etc.)
   * ✅ If backend says "busy" or "I don't know" on policy/company questions -> local fallback + correct links
   * ✅ Chat cards are compact inside the bubble (no giant cards)
   *
   * LANGUAGE RULES (This version):
   * ✅ All UI texts are EN
   * ✅ All fallback texts are EN
   * ✅ Default locale is browser language (fallback: EN)
   */

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else setTimeout(fn, 0);
  }

  function isMobile() {
    return window.innerWidth <= 768;
  }

  function escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  // ✅ Linkify: Convert URLs and phone numbers to clickable links
  // IMPORTANT: Do NOT call escapeHTML here - text is already plain/safe from typewriter
  function linkifyText(text) {
    if (!text) return '';

    let result = text;  // ✅ Use text directly, no escapeHTML!

    // 1. Linkify phone numbers (Turkish format: +90 XXX XXX XX XX)
    const phoneRegex = /(\+90\s?\d{3}\s?\d{3}\s?\d{2}\s?\d{2})/gi;
    result = result.replace(phoneRegex, function (phone) {
      const cleanPhone = phone.replace(/\s/g, ''); // Remove spaces
      return '<a href="https://wa.me/' + cleanPhone + '" target="_blank" rel="noopener noreferrer" style="color: #25D366; text-decoration: none; font-weight: 600;">📱 ' + phone + '</a>';
    });

    // 2. Linkify URLs (http, https, wa.me, etc.)
    const urlRegex = /(https?:\/\/[^\s<]+|wa\.me\/[^\s<]+)/gi;
    result = result.replace(urlRegex, function (url) {
      const fullUrl = url.startsWith('http') ? url : 'https://' + url;
      return '<a href="' + fullUrl + '" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline;">' + url + '</a>';
    });

    return result;
  }

  // ----------------------------
  // Typewriter animation
  // ----------------------------
  function stopTypewriter(el) {
    if (!el) return;
    if (el._gv_tw_timer) {
      clearInterval(el._gv_tw_timer);
      el._gv_tw_timer = null;
    }
    if (el._gv_tw_cleanup) {
      try { el._gv_tw_cleanup(); } catch (e) { }
      el._gv_tw_cleanup = null;
    }
    el.classList.remove('gv-typing');
  }

  function typewriter(el, text, opts) {
    if (!el) return;
    stopTypewriter(el);

    const speed = (opts && typeof opts.speed === 'number') ? opts.speed : 25;
    const onDone = opts && typeof opts.onDone === 'function' ? opts.onDone : null;
    const scrollEl = opts && opts.scrollEl ? opts.scrollEl : null;
    const autoScroll = opts && opts.autoScroll === true;

    const raw = (text == null) ? '' : String(text);
    const parts = raw.split(/\n/);

    el.classList.add('gv-typing');
    el.innerHTML = '';

    let lineIndex = 0;
    let charIndex = 0;

    function renderCurrent() {
      let html = '';
      for (let i = 0; i < parts.length; i++) {
        if (i < lineIndex) {
          html += escapeHTML(parts[i]) + '<br>';
        } else if (i === lineIndex) {
          html += escapeHTML(parts[i].slice(0, charIndex));
        }
      }
      el.innerHTML = html;
      if (autoScroll && scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
    }

    function tick() {
      if (lineIndex >= parts.length) {
        clearInterval(el._gv_tw_timer);
        el._gv_tw_timer = null;
        el.classList.remove('gv-typing');

        // ✅ Apply linkify WITHOUT re-escaping (text already escaped during render)
        const currentHTML = el.innerHTML;
        // Replace <br> with newlines temporarily
        const textWithNewlines = currentHTML.replace(/<br>/gi, '\n');
        // Create temp div to get plain text
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = textWithNewlines;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';
        // Apply linkify and restore <br>
        el.innerHTML = linkifyText(plainText).replace(/\n/g, '<br>');

        if (onDone) onDone();
        return;
      }

      const currentLine = parts[lineIndex] || '';
      if (charIndex <= currentLine.length) {
        charIndex++;
        renderCurrent();
      } else {
        lineIndex++;
        charIndex = 0;
        if (lineIndex < parts.length) {
          el.innerHTML = el.innerHTML + '<br>';
          if (autoScroll && scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
        }
      }
    }

    el._gv_tw_timer = setInterval(tick, Math.max(10, speed));
    el._gv_tw_cleanup = function () {
      if (el._gv_tw_timer) clearInterval(el._gv_tw_timer);
      el._gv_tw_timer = null;
      el.classList.remove('gv-typing');
    };
  }

  // ----------------------------
  // Shopify product data -> normalized context
  // ----------------------------
  function buildProductContextFromShopify(raw) {
    if (!raw || typeof raw !== 'object') return null;

    let imageUrls = null;
    if (Array.isArray(raw.images)) {
      imageUrls = raw.images
        .map(img => {
          if (!img) return null;
          if (typeof img === 'string') return img;
          return img.src || img.originalSrc || img.url || img.image || null;
        })
        .filter(Boolean);
      if (imageUrls.length === 0) imageUrls = null;
    }

    let variants = null;
    if (Array.isArray(raw.variants)) {
      variants = raw.variants.map(v => {
        let price = null;
        if (typeof v.price === 'string') price = v.price;
        else if (v.price && typeof v.price === 'object' && 'amount' in v.price) price = String(v.price.amount);
        else if (v.price != null) price = String(v.price);

        let compareAtPrice = null;
        if (typeof v.compare_at_price === 'string') compareAtPrice = v.compare_at_price;
        else if (v.compare_at_price && typeof v.compare_at_price === 'object' && 'amount' in v.compare_at_price) {
          compareAtPrice = String(v.compare_at_price.amount);
        } else if (v.compare_at_price != null) {
          compareAtPrice = String(v.compare_at_price);
        }

        const available = (typeof v.available === 'boolean')
          ? v.available
          : (typeof v.availableForSale === 'boolean' ? v.availableForSale : null);

        const invQ = (typeof v.inventory_quantity === 'number')
          ? v.inventory_quantity
          : (available ? 1 : 0);

        return {
          id: v.id != null ? Number(v.id) : null,
          sku: v.sku || null,
          title: v.title || null,
          price: price,
          compare_at_price: compareAtPrice,
          available: available,
          inventory_quantity: invQ
        };
      });
    }

    const descriptionHtml = raw.description_html || raw.description || null;

    return {
      title: raw.title || null,
      handle: raw.handle || null,
      url: window.location.href || null,
      description_html: descriptionHtml,
      tags: Array.isArray(raw.tags)
        ? raw.tags
        : (typeof raw.tags === 'string' ? raw.tags.split(',').map(t => t.trim()).filter(Boolean) : null),
      images: imageUrls,
      variants: variants
    };
  }

  // ----------------------------
  // Intent detection + Local Knowledge
  // ----------------------------
  function normTR(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/ı/g, 'i')
      .replace(/İ/g, 'i')
      .replace(/ş/g, 's')
      .replace(/Ş/g, 's')
      .replace(/ğ/g, 'g')
      .replace(/Ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/Ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/Ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/Ç/g, 'c')
      .trim();
  }

  function detectIntent(qRaw) {
    const q = normTR(qRaw);

    const wantsRecs =
      /(baska urun|benzer|alternatif|oner|oneri|farkli urun|muadil|suggest|recommend|recommendation|similar|alternative)/.test(q);

    const wantsCompany =
      /(sirket|hakkimizda|biz kimiz|hizmet|marka|grandeverse|interactive commerce|company|about|who are you|brand)/.test(q);

    const wantsReturns =
      /(iade|geri odeme|refund|return|exchange)/.test(q);

    const wantsShipping =
      /(kargo|teslimat|shipping|delivery|gonderim|gonderim suresi|kac gunde|ship|dispatch|arrive)/.test(q);

    const wantsPayment =
      /(odeme|checkout|hemen ode|buy now|siparis ver|satinal|satin al|odeme yapmak istiyorum|pay|payment|checkout)/.test(q);

    const wantsAddToCart =
      /(sepete ekle|sepetime ekle|add to cart|cart)/.test(q);

    return {
      wantsRecs,
      wantsCompany,
      wantsReturns,
      wantsShipping,
      wantsPayment,
      wantsAddToCart
    };
  }

  // Hard-coded canonical policy URLs
  const GV_POLICY_KB = {
    about: {
      title: 'About Us',
      url: 'https://adromfashion.com/pages/hakkimizda'
    },
    returns: {
      title: 'Returns & Refunds Policy',
      url: 'https://adromfashion.com/pages/iade-degisim',
      short_en: 'Most items can be returned, refunded, or exchanged within 30 days of delivery.'
    },
    shipping: {
      title: 'Shipping Policy',
      url: 'https://adromfashion.com/pages/kargo-teslimat',
      short_en: 'Orders are usually prepared within 1–3 business days. Shipping is currently available to U.S. addresses.'
    },
    privacy: {
      title: 'Privacy Policy',
      url: 'https://adromfashion.com/pages/gizlilik-politikasi'
    },
    terms: {
      title: 'Terms of Service',
      url: 'https://adromfashion.com/pages/kullanim-kosullari'
    },
    payment: {
      title: 'Payment & Security',
      url: 'https://adromfashion.com/pages/banka-hesap-bilgileri'
    }
  };

  function buildPolicyLinksForIntent(intent) {
    const links = [];
    if (intent.wantsReturns) links.push(GV_POLICY_KB.returns);
    if (intent.wantsShipping) links.push(GV_POLICY_KB.shipping);
    if (intent.wantsCompany) links.push(GV_POLICY_KB.about);
    if (!intent.wantsReturns && !intent.wantsShipping && !intent.wantsCompany) {
      links.push(GV_POLICY_KB.shipping, GV_POLICY_KB.returns, GV_POLICY_KB.privacy, GV_POLICY_KB.terms, GV_POLICY_KB.payment);
    }
    const seen = {};
    return links.filter(l => l && l.url && !seen[l.url] && (seen[l.url] = true));
  }

  function looksLikeBusyOrUnknown(answer) {
    const a = normTR(answer || '');
    if (!a) return true;
    return /(bilmiyorum|bilgim yok|cannot|cant|yogun|mesgul|try again|tekrar deneyin|uzgunum|i dont know|i do not know|busy|sorry)/.test(a);
  }

  // ----------------------------
  // Selected variant resolver (critical)
  // ----------------------------
  function getSelectedVariantIdFromPage() {
    // 1) URL ?variant=
    try {
      const u = new URL(window.location.href);
      const v = u.searchParams.get('variant');
      if (v && /^\d+$/.test(v)) return Number(v);
    } catch (e) { }

    // 2) Product form input/select name="id"
    try {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        const inp = form.querySelector('input[name="id"]');
        if (inp && inp.value && /^\d+$/.test(inp.value)) return Number(inp.value);

        const sel = form.querySelector('select[name="id"]');
        if (sel && sel.value && /^\d+$/.test(sel.value)) return Number(sel.value);
      }
    } catch (e) { }

    // 3) Shopify analytics meta
    try {
      const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      const id = meta && (meta.selectedVariantId || meta.selected_variant_id);
      if (id && /^\d+$/.test(String(id))) return Number(id);
    } catch (e) { }

    return null;
  }

  function buildLocalPurchaseCard(productCtx, selectedVariantId) {
    if (!productCtx) return null;

    const variants = Array.isArray(productCtx.variants) ? productCtx.variants : [];
    const vObj = selectedVariantId
      ? variants.find(v => Number(v.id) === Number(selectedVariantId))
      : (variants[0] || null);

    if (!vObj || !vObj.id) return null;

    return {
      title: productCtx.title || '',
      variant_title: vObj.title || '',
      variant_id: vObj.id,
      price: vObj.price != null ? String(vObj.price) : '',
      image_url: (Array.isArray(productCtx.images) && productCtx.images[0]) ? productCtx.images[0] : ''
    };
  }

  // ----------------------------
  // API: retry wrapper
  // ----------------------------
  async function postJSONWithRetry(url, payload, tries) {
    const maxTries = (typeof tries === 'number' && tries > 0) ? tries : 2;
    let lastErr = null;

    for (let i = 0; i < maxTries; i++) {
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('API Error ' + res.status);
        return await res.json();
      } catch (e) {
        lastErr = e;
        await new Promise(r => setTimeout(r, 180 + (i * 220)));
      }
    }
    throw lastErr || new Error('API Error');
  }

  // ----------------------------
  // Post-process backend response (UX hardening)
  // ----------------------------
  function postProcessResponse(CONFIG, question, productCtx, data) {
    const intent = detectIntent(question || '');
    const selectedVariantId = getSelectedVariantIdFromPage();
    const localCard = buildLocalPurchaseCard(productCtx, selectedVariantId);

    const out = Object.assign({}, data || {});
    out.answer = (out && out.answer != null) ? String(out.answer) : '';

    // 1) If payment/add-to-cart intent and backend card is missing/wrong -> force local card
    if ((intent.wantsPayment || intent.wantsAddToCart) && localCard && localCard.variant_id) {
      out.purchase_card = localCard;
    }

    // 2) If policy/company intent but backend returns busy/unknown -> local fallback + correct links
    if (looksLikeBusyOrUnknown(out.answer) && (intent.wantsCompany || intent.wantsReturns || intent.wantsShipping)) {
      if (intent.wantsCompany) {
        out.answer =
          "Quick info about GrandeVerse™: We offer selected products across different categories through our brand. " +
          "For full company and contact details, please visit the About Us page.";
      } else if (intent.wantsReturns) {
        out.answer =
          "Returns & refunds (summary): Most items are eligible for a return, refund, or exchange within 30 days of delivery. " +
          "Please review the full policy for conditions and exceptions.";
      } else if (intent.wantsShipping) {
        out.answer =
          "Shipping (summary): Orders are usually prepared within 1–3 business days. Shipping timelines and conditions are listed in our Shipping Policy.";
      }
      out.policy_links = buildPolicyLinksForIntent(intent);
    }

    // 3) If backend policy_links is empty but the question is policy-related -> add local links
    const hasPolicy = Array.isArray(out.policy_links) && out.policy_links.length > 0;
    if (!hasPolicy && (intent.wantsReturns || intent.wantsShipping || /policy|politika/.test(normTR(question || '')))) {
      out.policy_links = buildPolicyLinksForIntent(intent);
    }

    // 4) Recommendation rule: ONLY if user asks. Also hide if answer says cannot recommend.
    const answerNorm = normTR(out.answer);
    const saysCannotRecommend = /(oneremem|baska urun oneremiyorum|cannot recommend|can't recommend|i can not recommend|i cannot recommend)/.test(answerNorm);

    if (!intent.wantsRecs || saysCannotRecommend) {
      out.recommended_products = [];
    }

    // 5) If payment intent exists, add a helpful preface showing the selected variant
    if ((intent.wantsPayment || intent.wantsAddToCart) && localCard && localCard.variant_title) {
      const preface = "Done — I've prepared the action for your selected variant: " + localCard.variant_title + ".\n";
      if (out.answer && !out.answer.startsWith(preface)) out.answer = preface + out.answer;
      if (!out.answer) out.answer = preface;
    }

    return out;
  }

  // ----------------------------
  // Bootstrap
  // ----------------------------
  onReady(function () {
    try {
      initNova();
      if (typeof Shopify !== 'undefined') {
        document.addEventListener('shopify:section:load', initNova);
        document.addEventListener('shopify:section:reorder', initNova);
        document.addEventListener('shopify:section:select', initNova);
      }
    } catch (error) {
      console.error('Grandeverse AI init error:', error);
    }
  });

  function initNova() {
    const root = document.getElementById('grandeverse-ai-root');
    if (!root) return;

    cleanupExistingUI();

    const rawProduct =
      (window.GrandeverseProduct && typeof window.GrandeverseProduct === 'object')
        ? window.GrandeverseProduct
        : null;

    const normalizedProductContext = buildProductContextFromShopify(rawProduct);

    const browserLocale = (function () {
      try {
        const lang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
        const short = lang.split('-')[0] || 'en';
        return short || 'en';
      } catch (e) {
        return 'en';
      }
    })();

    const localeFromDOM = (root.dataset && root.dataset.locale) ? String(root.dataset.locale).toLowerCase() : '';

    const CONFIG = {
      logoUrl: 'https://cdn.shopify.com/s/files/1/0638/4205/8311/files/grandeverse_bg_0d1b2a.png?v=1762176567',
      aiName: 'Cann',
      aiLabel: 'Grandeverse Intelligence',
      productId: parseInt(root.dataset.productId, 10) || 0,

      // ✅ Default locale: DOM data-locale if present, else browser language. Fallback: EN.
      locale: (localeFromDOM || browserLocale || 'en'),

      endpoint: 'https://api.toptan.interaktif.tech/product-chat',
      productContext: normalizedProductContext,

      // ✅ ALL UI TEXTS IN EN
      text: {
        btnLabel: 'Soru Sor',
        btnLabelActive: 'Kapat',
        placeholder: 'Sorunuzu yazın...',
        sendButtonText: 'Gönder',
        error: 'Üzgünüz, bir hata oluştu. Lütfen tekrar deneyin.',
        greeting: 'Merhaba! Size nasıl yardımcı olabilirim?',
        policyLinksTitle: 'İlgili Politikalar',
        recommendedProductsTitle: 'Önerilen Ürünler',
        addToCart: 'Sepete Ekle',
        buyNow: 'Hemen Al',
        outOfStock: 'Stokta Yok',
        price: 'Fiyat',
        compareAtPrice: 'İndirimli Fiyat',
        variant: 'Varyant',
        inputPlaceholder: 'Bir soru sorun...',
        mobilePlaceholder: 'Bir soru sorun...',
        welcomeTitle: 'Merhaba!',
        welcomeMsg: "Ürün detayları, uyumluluk, kullanım ve doğru seçeneği bulma konusunda size yardımcı olabilirim.",
        minimizedThinking: 'Düşünüyor...',
        minimizedAnswering: 'Cevaplıyor...',
        minimizedReady: '1 yeni mesaj',
        mobileAskTitle: 'Soru Sor',
        mobileAskHint: 'Sorunuzu yazın, anında cevap alın.',
        mobileLoading: 'Cevabınız hazırlanıyor...'
      },

      debug: false,
      moveUnderDescriptionOnMobile: true,
      typewriterSpeed: 22,
      autoScrollDuringTypewriter: true,
      initialQuestionsCountHint: 5
    };

    injectFontLinks();
    injectStyles();

    const dom = createDOM(CONFIG);
    root.appendChild(dom.widgetContainer);

    // Delegate: Add to cart + Buy Now buttons
    document.addEventListener('click', function (e) {
      const addBtn = e.target && e.target.closest && e.target.closest('.gv-btn-add-to-cart');
      const buyBtn = e.target && e.target.closest && e.target.closest('.gv-btn-buy-now');
      const btn = addBtn || buyBtn;
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      const isBuyNow = !!buyBtn;
      addToCart(variantId, btn, { buyNow: isBuyNow });
    });

    async function addToCart(variantId, btnElement, opts) {
      const buyNow = opts && opts.buyNow === true;

      const originalText = btnElement.innerHTML;
      btnElement.innerHTML = '<span class="gv-spin">↻</span> ' + (buyNow ? 'Redirecting...' : 'Adding...');
      btnElement.style.opacity = '0.85';
      btnElement.style.pointerEvents = 'none';

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        });

        if (!response.ok) throw new Error('Cart API error');

        if (buyNow) {
          window.location.href = window.Shopify.routes.root + 'checkout';
          return;
        }

        btnElement.innerHTML = '✔ Added to Cart';
        btnElement.classList.add('gv-added');

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:add', { bubbles: true }));

      } catch (err) {
        console.error('Add/buy error:', err);
        btnElement.innerHTML = '❌ Error';
        setTimeout(() => {
          btnElement.innerHTML = originalText;
          btnElement.style.opacity = '1';
          btnElement.style.pointerEvents = 'auto';
          btnElement.classList.remove('gv-added');
        }, 1800);
      }
    }

    // Global pills click (mobile + desktop)
    dom.pillsWrap.addEventListener('click', function (e) {
      const pill = e.target && e.target.closest && e.target.closest('.gv-pill');
      if (!pill) return;
      const txt = pill.textContent ? pill.textContent.trim() : '';
      if (!txt) return;

      if (isMobile()) {
        dom.inlinePanel.style.display = 'flex';
        dom.inlineInput.value = txt;
        dom.inlineSendBtn.click();
        setTimeout(() => dom.inlineInput.focus(), 120);
      } else {
        dom.mainBtn.click();
        setTimeout(() => {
          dom.sidebarInput.value = txt;
          dom.sidebarSendBtn.click();
        }, 200);
      }
    });

    // Mobile inline chat
    if (isMobile()) {
      setupMobileInline(CONFIG, dom);
      if (CONFIG.moveUnderDescriptionOnMobile) setTimeout(() => relocateForMobile(root), 100);
      fetchInitialSuggestions(CONFIG, dom, normalizedProductContext);
      return;
    }

    // Desktop sidebar chat
    setupDesktopSidebar(CONFIG, dom);
    if (CONFIG.moveUnderDescriptionOnMobile) setTimeout(() => relocateForMobile(root), 100);
    fetchInitialSuggestions(CONFIG, dom, normalizedProductContext);
  }

  // ----------------------------
  // Mobile inline panel
  // ----------------------------
  function setupMobileInline(CONFIG, dom) {
    let inFlight = false;

    dom.inlinePanel.style.display = 'none';
    dom.inlineAnswer.style.display = 'none';
    dom.inlineAnswer.innerHTML = '';

    dom.mainBtn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      const isOpen = dom.inlinePanel.style.display === 'flex';
      dom.inlinePanel.style.display = isOpen ? 'none' : 'flex';
      if (!isOpen) setTimeout(() => dom.inlineInput.focus(), 120);
    });

    dom.inlineSendBtn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      handleInlineSend();
    });

    dom.inlineInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault(); e.stopPropagation();
        handleInlineSend();
      }
    });

    dom.inlineInput.addEventListener('input', function () {
      this.style.height = 'auto';
      this.style.height = Math.min(this.scrollHeight, 120) + 'px';
    });

    async function handleInlineSend() {
      if (inFlight) return;
      const q = (dom.inlineInput.value || '').trim();
      if (!q) return;

      inFlight = true;
      dom.inlineInput.value = '';
      dom.inlineInput.style.height = 'auto';

      dom.inlineAnswer.style.display = 'block';
      stopTypewriter(dom.inlineAnswer);
      dom.inlineAnswer.innerHTML = `<div class="gv-inline-loading-text">${escapeHTML(CONFIG.text.mobileLoading)}</div>`;
      dom.inlineAnswer.scrollTop = dom.inlineAnswer.scrollHeight;

      try {
        const rawProduct = window.GrandeverseProduct;
        const currentContext = buildProductContextFromShopify(rawProduct);

        const data = await postJSONWithRetry(CONFIG.endpoint, {
          product_id: CONFIG.productId,
          question: q,
          language: CONFIG.locale,
          product_context: currentContext
        }, 2);

        const fixed = postProcessResponse(CONFIG, q, currentContext, data);
        renderInlineAnswer(CONFIG, dom, fixed, q);

      } catch (err) {
        console.error(err);
        const intent = detectIntent(q);
        if (intent.wantsCompany || intent.wantsReturns || intent.wantsShipping) {
          const fallback = postProcessResponse(CONFIG, q, buildProductContextFromShopify(window.GrandeverseProduct), { answer: '' });
          renderInlineAnswer(CONFIG, dom, fallback, q);
        } else {
          dom.inlineAnswer.innerHTML = `<div class="gv-inline-error">${escapeHTML(CONFIG.text.error)}</div>`;
        }
      } finally {
        inFlight = false;
      }
    }
  }

  function renderInlineAnswer(CONFIG, dom, data, question) {
    const answer = (data && data.answer) ? String(data.answer) : '';
    const recos = (data && Array.isArray(data.recommended_products)) ? data.recommended_products : [];
    const card = (data && data.purchase_card) ? data.purchase_card : null;
    const policyLinks = (data && Array.isArray(data.policy_links)) ? data.policy_links : [];

    const wrap = document.createElement('div');
    wrap.className = 'gv-inline-answer-wrap';

    const answerEl = document.createElement('div');
    answerEl.className = 'gv-inline-answer-text';
    wrap.appendChild(answerEl);

    const extras = document.createElement('div');
    extras.className = 'gv-inline-extras';
    wrap.appendChild(extras);

    dom.inlineAnswer.innerHTML = '';
    dom.inlineAnswer.appendChild(wrap);

    typewriter(answerEl, answer, {
      speed: CONFIG.typewriterSpeed,
      scrollEl: dom.inlineAnswer,
      autoScroll: true,
      onDone: function () {
        if (policyLinks.length > 0) extras.appendChild(buildPolicyLinks('Related Policies', policyLinks));
        if (recos.length > 0) extras.appendChild(buildProductCards('Recommended Products', recos));
        if (card && card.variant_id) extras.appendChild(buildPurchaseCard(card));
        dom.inlineAnswer.scrollTop = dom.inlineAnswer.scrollHeight;
      }
    });
  }

  // ----------------------------
  // Desktop sidebar panel
  // ----------------------------
  function setupDesktopSidebar(CONFIG, dom) {
    document.body.appendChild(dom.sidebar);
    document.body.appendChild(dom.overlay);
    document.body.appendChild(dom.minimizedBalloon);

    let sidebarState = 'closed';
    let isRequestInFlight = false;

    const headerEl = dom.sidebar.querySelector('.gv-header');
    if (dom.sidebar && headerEl) makeDraggable(dom.sidebar, headerEl);

    function setState(newState) {
      sidebarState = newState;

      if (newState === 'open') {
        dom.sidebar.classList.add('active');
        dom.sidebar.style.display = 'flex';
        dom.overlay.classList.add('active');
        dom.minimizedBalloon.classList.remove('active');
        document.body.classList.add('gv-sidebar-open');
        document.body.classList.remove('gv-sidebar-minimized');
        setTimeout(() => dom.sidebarInput.focus(), 250);
      }

      if (newState === 'minimized') {
        dom.sidebar.classList.remove('active');
        dom.sidebar.style.display = 'none';
        dom.overlay.classList.remove('active');
        dom.minimizedBalloon.classList.add('active');
        document.body.classList.remove('gv-sidebar-open');
        document.body.classList.add('gv-sidebar-minimized');
      }

      if (newState === 'closed') {
        dom.sidebar.classList.remove('active');
        dom.sidebar.style.display = 'none';
        dom.overlay.classList.remove('active');
        dom.minimizedBalloon.classList.remove('active');
        document.body.classList.remove('gv-sidebar-open', 'gv-sidebar-minimized');
      }
    }

    function setupEventListeners() {
      dom.mainBtn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); setState('open'); });
      dom.minimizeBtn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); setState('minimized'); });
      dom.closeBtn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); setState('closed'); });
      dom.overlay.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); if (sidebarState === 'open') setState('minimized'); });

      dom.minimizedBalloon.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (sidebarState === 'minimized') setState('open');
      });

      dom.sidebarSendBtn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); handleSend(); });
      dom.sidebarInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault(); e.stopPropagation();
          handleSend();
        }
      });
      dom.sidebarInput.addEventListener('input', function () {
        this.style.height = 'auto';
        this.style.height = Math.min(this.scrollHeight, 100) + 'px';
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          if (sidebarState === 'open') setState('minimized');
          else if (sidebarState === 'minimized') setState('closed');
        }
      });
    }

    function addMessage(role, text, isError, suggestedQuestions) {
      const row = document.createElement('div');
      row.className = `gv-row gv-row-${role}`;

      if (role === 'ai') {
        const avatar = document.createElement('div');
        avatar.className = 'gv-avatar-ai';
        avatar.textContent = '✨';
        row.appendChild(avatar);

        const bubble = document.createElement('div');
        bubble.className = `gv-bubble ${isError ? 'gv-bubble-error' : ''}`;

        const textEl = document.createElement('div');
        textEl.className = 'gv-bubble-text';
        // ✅ Use linkifyText to make URLs clickable
        textEl.innerHTML = linkifyText(text).replace(/\n/g, '<br>');
        bubble.appendChild(textEl);

        row.appendChild(bubble);

        // ✅ Render suggested questions immediately below AI answer
        if (suggestedQuestions && suggestedQuestions.length > 0) {
          const questionsWrap = document.createElement('div');
          questionsWrap.className = 'gv-suggested-questions';
          questionsWrap.style.cssText = 'display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; margin-left: 40px;';

          suggestedQuestions.forEach(function (q) {
            const pill = document.createElement('button');
            pill.className = 'gv-suggested-pill';
            pill.style.cssText = 'background: #f0f0f0; border: 1px solid #ddd; border-radius: 16px; padding: 6px 14px; font-size: 13px; cursor: pointer; transition: all 0.2s;';
            pill.textContent = q;
            pill.onmouseover = function () { this.style.background = '#e0e0e0'; };
            pill.onmouseout = function () { this.style.background = '#f0f0f0'; };
            pill.onclick = function () {
              if (dom.sidebarInput) {
                dom.sidebarInput.value = q;
                dom.sidebarSendBtn.click();
              }
            };
            questionsWrap.appendChild(pill);
          });

          row.appendChild(questionsWrap);
        }
      } else {
        const bubble = document.createElement('div');
        bubble.className = 'gv-bubble';
        bubble.innerHTML = escapeHTML(text).replace(/\n/g, '<br>');
        row.appendChild(bubble);
      }

      dom.msgList.appendChild(row);
      scrollToBottom(dom.chatBody);
    }

    function addTyping() {
      const id = 'gv-typing-' + Date.now();
      const row = document.createElement('div');
      row.className = 'gv-row gv-row-ai';
      row.id = id;
      row.innerHTML = `
        <div class="gv-avatar-ai">✨</div>
        <div class="gv-bubble gv-bubble-typing">
          <span class="gv-dot"></span><span class="gv-dot"></span><span class="gv-dot"></span>
        </div>
      `;
      dom.msgList.appendChild(row);
      scrollToBottom(dom.chatBody);
      return id;
    }

    function removeTyping(id) {
      const el = document.getElementById(id);
      if (el) el.remove();
    }

    async function handleSend() {
      if (isRequestInFlight) return;
      const question = (dom.sidebarInput.value || '').trim();
      if (!question) return;

      dom.sidebarInput.value = '';
      dom.sidebarInput.style.height = 'auto';

      isRequestInFlight = true;
      addMessage('user', question);

      const typingId = addTyping();

      try {
        const welcome = dom.sidebar.querySelector('.gv-welcome-card');
        if (welcome) welcome.style.display = 'none';

        const rawProduct = window.GrandeverseProduct;
        const currentContext = buildProductContextFromShopify(rawProduct);

        const data = await postJSONWithRetry(CONFIG.endpoint, {
          product_id: CONFIG.productId,
          question: question,
          language: CONFIG.locale,
          product_context: currentContext
        }, 2);

        const fixed = postProcessResponse(CONFIG, question, currentContext, data);

        removeTyping(typingId);
        addAIMessage(CONFIG, dom, fixed, question);

      } catch (err) {
        console.error(err);
        removeTyping(typingId);

        const intent = detectIntent(question);
        if (intent.wantsCompany || intent.wantsReturns || intent.wantsShipping) {
          const fallback = postProcessResponse(CONFIG, question, buildProductContextFromShopify(window.GrandeverseProduct), { answer: '' });
          addAIMessage(CONFIG, dom, fallback, question);
        } else {
          addMessage('ai', CONFIG.text.error, true);
        }
      } finally {
        isRequestInFlight = false;
      }
    }

    setupEventListeners();
    setState('closed');
  }

  function addAIMessage(CONFIG, dom, data, question) {
    const answer = (data && data.answer) ? String(data.answer) : '';
    const followups = (data && Array.isArray(data.suggested_followups)) ? data.suggested_followups : [];
    const recos = (data && Array.isArray(data.recommended_products)) ? data.recommended_products : [];
    const pcard = (data && data.purchase_card) ? data.purchase_card : null;
    const policyLinks = (data && Array.isArray(data.policy_links)) ? data.policy_links : [];

    const row = document.createElement('div');
    row.className = 'gv-row gv-row-ai';

    const avatar = document.createElement('div');
    avatar.className = 'gv-avatar-ai';
    avatar.textContent = '✨';
    row.appendChild(avatar);

    const bubble = document.createElement('div');
    bubble.className = 'gv-bubble';

    const textEl = document.createElement('div');
    textEl.className = 'gv-bubble-text';
    bubble.appendChild(textEl);

    const extras = document.createElement('div');
    extras.className = 'gv-bubble-extra';
    bubble.appendChild(extras);

    row.appendChild(bubble);
    dom.msgList.appendChild(row);
    scrollToBottom(dom.chatBody);

    typewriter(textEl, answer, {
      speed: CONFIG.typewriterSpeed,
      scrollEl: dom.chatBody,
      autoScroll: CONFIG.autoScrollDuringTypewriter,
      onDone: function () {
        // ✅ Extract and render suggested_questions
        const suggestedQuestions = (data && Array.isArray(data.suggested_questions)) ? data.suggested_questions : [];

        if (suggestedQuestions.length > 0) {
          const questionsWrap = document.createElement('div');
          questionsWrap.className = 'gv-suggested-questions';
          questionsWrap.style.cssText = 'display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px;';

          suggestedQuestions.forEach(function (q) {
            const pill = document.createElement('button');
            pill.className = 'gv-suggested-pill';
            pill.style.cssText = 'background: #f0f0f0; border: 1px solid #ddd; border-radius: 16px; padding: 6px 14px; font-size: 13px; cursor: pointer; transition: all 0.2s;';
            pill.textContent = q;
            pill.onmouseover = function () { this.style.background = '#e0e0e0'; };
            pill.onmouseout = function () { this.style.background = '#f0f0f0'; };
            pill.onclick = function () {
              if (dom.sidebarInput) {
                dom.sidebarInput.value = q;
                dom.sidebarSendBtn.click();
              }
            };
            questionsWrap.appendChild(pill);
          });

          extras.appendChild(questionsWrap);
        }

        if (policyLinks.length > 0) extras.appendChild(buildPolicyLinks('Related Policies', policyLinks));

        if (followups.length > 0) {
          const pillsContainer = document.createElement('div');
          pillsContainer.className = 'gv-followup-pills gv-scrollable-pills';
          followups.forEach(function (q) {
            const pill = document.createElement('div');
            pill.className = 'gv-pill gv-followup-pill';
            pill.innerHTML = `<span>${escapeHTML(String(q))}</span>`;
            pill.onclick = (e) => {
              e.stopPropagation();
              dom.sidebarInput.value = String(q);
              dom.sidebarSendBtn.click();
            };
            pillsContainer.appendChild(pill);
          });
          extras.appendChild(pillsContainer);
        }

        if (recos.length > 0) extras.appendChild(buildProductCards('Recommended Products', recos));
        if (pcard && pcard.variant_id) extras.appendChild(buildPurchaseCard(pcard));
      }
    });
  }

  async function fetchInitialSuggestions(CONFIG, dom, normalizedProductContext) {
    try {
      dom.pillsWrap.classList.add('gv-loading-state');
      const data = await postJSONWithRetry(CONFIG.endpoint, {
        product_id: CONFIG.productId,
        question: null,
        language: CONFIG.locale,
        product_context: normalizedProductContext
      }, 2);

      renderPills(dom, data && data.suggested_questions ? data.suggested_questions : []);
    } catch (error) {
      renderPills(dom, []);
    }
  }

  function renderPills(dom, questions) {
    dom.pillsWrap.classList.remove('gv-loading-state');
    dom.pillsWrap.innerHTML = '';
    (questions || []).forEach(function (question) {
      const pill = document.createElement('div');
      pill.className = 'gv-pill';
      pill.innerHTML = `<span>${escapeHTML(String(question))}</span>`;
      dom.pillsWrap.appendChild(pill);
    });
  }

  function buildProductCards(title, products) {
    const wrap = document.createElement('div');
    wrap.className = 'gv-prod-cards-wrap';

    const h = document.createElement('div');
    h.className = 'gv-prod-cards-title';
    h.textContent = title || 'Recommendations';
    wrap.appendChild(h);

    const list = document.createElement('div');
    list.className = 'gv-prod-cards';

    (products || []).forEach(p => {
      const card = document.createElement('div');
      card.className = 'gv-prod-card';

      const img = document.createElement('img');
      img.className = 'gv-prod-img';
      img.src = p.image_url || '';
      img.alt = p.title || 'Product';
      card.appendChild(img);

      const meta = document.createElement('div');
      meta.className = 'gv-prod-meta';

      const t = document.createElement('div');
      t.className = 'gv-prod-name';
      t.textContent = p.title || '';
      meta.appendChild(t);

      if (p.variant_title) {
        const vt = document.createElement('div');
        vt.className = 'gv-prod-variant';
        vt.textContent = String(p.variant_title);
        meta.appendChild(vt);
      }

      const pr = document.createElement('div');
      pr.className = 'gv-prod-price';
      pr.textContent = p.price ? String(p.price) : '';
      meta.appendChild(pr);

      const actions = document.createElement('div');
      actions.className = 'gv-prod-actions';

      if (p.url) {
        const a = document.createElement('a');
        a.className = 'gv-prod-link';
        a.href = p.url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = 'View';
        actions.appendChild(a);
      }

      if (p.variant_id) {
        const b = document.createElement('button');
        b.className = 'gv-btn-add-to-cart gv-prod-add';
        b.setAttribute('data-variant-id', String(p.variant_id));
        b.textContent = 'Add to Cart';
        actions.appendChild(b);
      }

      meta.appendChild(actions);
      card.appendChild(meta);
      list.appendChild(card);
    });

    wrap.appendChild(list);
    return wrap;
  }

  function buildPurchaseCard(card) {
    const wrap = document.createElement('div');
    wrap.className = 'gv-purchase-card';

    const img = document.createElement('img');
    img.className = 'gv-purchase-img';
    img.src = card.image_url || '';
    img.alt = card.title || 'Product';
    wrap.appendChild(img);

    const meta = document.createElement('div');
    meta.className = 'gv-purchase-meta';

    const t = document.createElement('div');
    t.className = 'gv-purchase-title';
    t.textContent = card.title || '';
    meta.appendChild(t);

    if (card.variant_title) {
      const vt = document.createElement('div');
      vt.className = 'gv-purchase-variant';
      vt.textContent = String(card.variant_title);
      meta.appendChild(vt);
    }

    const pr = document.createElement('div');
    pr.className = 'gv-purchase-price';
    pr.textContent = card.price ? String(card.price) : '';
    meta.appendChild(pr);

    const actions = document.createElement('div');
    actions.className = 'gv-purchase-actions';

    const b = document.createElement('button');
    b.className = 'gv-btn-add-to-cart';
    b.setAttribute('data-variant-id', String(card.variant_id));
    b.textContent = 'Add to Cart';
    actions.appendChild(b);

    const bn = document.createElement('button');
    bn.className = 'gv-btn-buy-now';
    bn.setAttribute('data-variant-id', String(card.variant_id));
    bn.textContent = 'Buy Now';
    actions.appendChild(bn);

    meta.appendChild(actions);

    wrap.appendChild(meta);
    return wrap;
  }

  function buildPolicyLinks(title, links) {
    const wrap = document.createElement('div');
    wrap.className = 'gv-policy-links';

    const h = document.createElement('div');
    h.className = 'gv-policy-title';
    h.textContent = title || 'Policies';
    wrap.appendChild(h);

    (links || []).forEach(l => {
      if (!l || !l.url) return;
      const a = document.createElement('a');
      a.className = 'gv-policy-link';
      a.href = String(l.url);
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = l.title ? String(l.title) : String(l.url);
      wrap.appendChild(a);
    });

    return wrap;
  }

  function createDOM(config) {
    const logoHTML = config.logoUrl
      ? `<img src="${config.logoUrl}" class="gv-logo-img" alt="GV Logo">`
      : `<div class="gv-logo-fallback"><span>G</span><span>V</span></div>`;

    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'gv-widget-area';
    widgetContainer.setAttribute('data-gv-widget', '1');
    widgetContainer.innerHTML = `
      <div class="gv-nova-trigger">
        <div class="gv-nova-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" fill="currentColor"/>
          </svg>
        </div>
        <div class="gv-nova-text">
          <span class="gv-nova-name">${escapeHTML(config.text.btnLabel)}</span>
          <span class="gv-nova-sub">Adrom Fashion tarafından desteklenmektedir</span>
        </div>
        <div class="gv-nova-arrow">→</div>
      </div>

      <div class="gv-pills-wrapper gv-loading-state">
        <div class="gv-skel"></div><div class="gv-skel"></div><div class="gv-skel"></div>
      </div>

      <div class="gv-inline-panel">
        <div class="gv-inline-head">
          <div class="gv-inline-title">${escapeHTML(config.text.mobileAskTitle)}</div>
          <div class="gv-inline-hint">${escapeHTML(config.text.mobileAskHint)}</div>
        </div>

        <div class="gv-inline-answer"></div>

        <div class="gv-inline-inputbox">
          <textarea class="gv-inline-inp" rows="1" placeholder="${escapeHTML(config.text.mobilePlaceholder)}"></textarea>
          <button class="gv-inline-send" type="button">Send</button>
        </div>
      </div>
    `;

    const sidebar = document.createElement('div');
    sidebar.className = 'gv-sidebar';
    sidebar.setAttribute('data-gv-sidebar', '1');
    sidebar.innerHTML = `
      <div class="gv-header">
        <div class="gv-brand">
          ${logoHTML}
          <div class="gv-brand-info">
            <div class="gv-brand-name">${escapeHTML(config.aiName)} <span class="gv-beta-tag">BETA</span></div>
            <div class="gv-brand-sub">${escapeHTML(config.aiLabel)}</div>
          </div>
        </div>
        <div class="gv-header-controls">
          <div class="gv-minimize-icon" title="Minimize">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
              <path d="M15 11H9v2h6v-2z"/>
            </svg>
          </div>
          <div class="gv-close-icon" title="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </div>
        </div>
      </div>

      <div class="gv-chat-body">
        <div class="gv-welcome-card">
          <div class="gv-welcome-icon">✨</div>
          <h3>${escapeHTML(config.text.welcomeTitle)}</h3>
          <p>${escapeHTML(config.text.welcomeMsg)}</p>
        </div>
        <div class="gv-messages-list"></div>
      </div>

      <div class="gv-footer">
        <div class="gv-input-box">
          <textarea class="gv-sidebar-inp" rows="1" placeholder="${escapeHTML(config.text.inputPlaceholder)}"></textarea>
          <div class="gv-send-btn" role="button" aria-label="Send">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z"/>
            </svg>
          </div>
        </div>
        <div class="gv-disclaimer">Cann may make mistakes. Please verify important information.</div>
      </div>
    `;

    const overlay = document.createElement('div');
    overlay.className = 'gv-sidebar-overlay';

    const minimizedBalloon = document.createElement('div');
    minimizedBalloon.className = 'gv-minimized-balloon';
    minimizedBalloon.innerHTML = `
      <div class="gv-minimized-content">
        <div class="gv-minimized-avatar">✨</div>
        <div class="gv-minimized-info">
          <div class="gv-minimized-title">${escapeHTML(config.aiName)}</div>
        </div>
        <div class="gv-minimized-badge">1</div>
      </div>
    `;

    return {
      widgetContainer,
      mainBtn: widgetContainer.querySelector('.gv-nova-trigger'),
      pillsWrap: widgetContainer.querySelector('.gv-pills-wrapper'),

      inlinePanel: widgetContainer.querySelector('.gv-inline-panel'),
      inlineAnswer: widgetContainer.querySelector('.gv-inline-answer'),
      inlineInput: widgetContainer.querySelector('.gv-inline-inp'),
      inlineSendBtn: widgetContainer.querySelector('.gv-inline-send'),

      sidebar,
      minimizeBtn: sidebar.querySelector('.gv-minimize-icon'),
      closeBtn: sidebar.querySelector('.gv-close-icon'),
      chatBody: sidebar.querySelector('.gv-chat-body'),
      msgList: sidebar.querySelector('.gv-messages-list'),
      sidebarInput: sidebar.querySelector('.gv-sidebar-inp'),
      sidebarSendBtn: sidebar.querySelector('.gv-send-btn'),

      overlay,
      minimizedBalloon
    };
  }

  function relocateForMobile(root) {
    if (window.innerWidth > 768) return;
    const descSelectors = [
      '.product-block--description',
      '.product__description',
      '[data-product-description]',
      '.description',
      '.Product__Description'
    ];
    let descBlock = null;
    for (const selector of descSelectors) {
      descBlock = document.querySelector(selector);
      if (descBlock) break;
    }
    if (!descBlock) return;

    const aiWrapper = root.closest('.gv-ai-under-gallery') || root;
    if (aiWrapper.dataset.gvMoved === '1' || !descBlock.parentNode) return;

    try {
      descBlock.parentNode.insertBefore(aiWrapper, descBlock);
      aiWrapper.dataset.gvMoved = '1';
    } catch (e) { }
  }

  function cleanupExistingUI() {
    document.querySelectorAll('.gv-widget-area[data-gv-widget="1"]').forEach(w => w.remove());
    document.querySelectorAll('.gv-sidebar[data-gv-sidebar="1"]').forEach(s => s.remove());
    document.querySelectorAll('.gv-sidebar-overlay').forEach(o => o.remove());
    document.querySelectorAll('.gv-minimized-balloon').forEach(b => b.remove());
    document.body.classList.remove('gv-sidebar-open', 'gv-sidebar-minimized');
  }

  function injectFontLinks() {
    if (document.getElementById('gv-inter-font')) return;
    const link = document.createElement('link');
    link.id = 'gv-inter-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }

  function injectStyles() {
    if (document.getElementById('gv-nova-styles')) return;
    const styles = document.createElement('style');
    styles.id = 'gv-nova-styles';
    styles.textContent = `
      :root {
        --gv-font: Inter, -apple-system, sans-serif;
        --gv-panel-w: 380px;
        --gv-bg-dark: #0a192f;
        --gv-accent: #FFC107;
        --gv-gray-50: #f9fafb;
        --gv-gray-200: #e5e7eb;
        --gv-gray-800: #1f2937;
        --gv-navy: #0f172a;
      }

      .gv-widget-area { font-family: var(--gv-font); margin: 1.5rem 0; box-sizing: border-box; width: 100%; }

      .gv-typing:after {
        content: '|';
        display: inline-block;
        margin-left: 2px;
        color: var(--gv-accent);
        animation: gv-blink 1s step-start infinite;
      }
      @keyframes gv-blink { 50% { opacity: 0; } }

      .gv-pills-wrapper { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px; width: 100%; }
      .gv-pill {
        background: #fff;
        border: 1.5px solid #e2e8f0;
        border-radius: 24px;
        padding: 10px 14px;
        font-size: 13px;
        font-weight: 600;
        color: #334155;
        cursor: pointer;
        transition: all 0.2s ease;
        user-select: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        min-height: 42px;
        max-width: 100%;
        white-space: normal;
      }
      .gv-pill:hover { transform: translateY(-1px); border-color: var(--gv-accent); }

      @media (max-width: 768px) {
        .gv-pills-wrapper { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 12px; margin-top: 12px; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
        .gv-pills-wrapper::-webkit-scrollbar { display: none; }
        .gv-pill { flex-shrink: 0; min-width: max-content; max-width: 260px; }
      }

      .gv-nova-trigger {
        display: flex; align-items: center; width: 100%; max-width: 420px;
        background: linear-gradient(180deg, #0f172a 0%, #0a101d 100%);
        color: white;
        border: 1px solid rgba(255,255,255,0.12);
        border-left: 4px solid var(--gv-accent);
        border-radius: 8px;
        padding: 14px 18px;
        cursor: pointer;
        box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.4), 0 4px 10px -2px rgba(0,0,0,0.2);
        transition: all 0.2s ease;
      }
      .gv-nova-trigger:hover { transform: translateY(-1px); }
      .gv-nova-icon {
        background: rgba(255,255,255,0.1);
        width: 32px; height: 32px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 50%;
        margin-right: 14px;
        color: var(--gv-accent);
      }
      .gv-nova-name { font-weight: 700; font-size: 15px; display: block; line-height: 1.2; }
      .gv-nova-sub { font-size: 11px; color: rgba(255,255,255,0.65); display: block; margin-top: 2px; }
      .gv-nova-arrow { margin-left: auto; font-size: 18px; color: rgba(255,255,255,0.4); }

      /* MOBILE INLINE PANEL */
      .gv-inline-panel {
        display: none;
        margin-top: 12px;
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 14px;
        padding: 12px;
        box-shadow: 0 6px 18px rgba(0,0,0,0.06);
        flex-direction: column;
        max-height: 60vh; 
        overflow: hidden;
      }
      @media (min-width: 769px) { .gv-inline-panel { display: none !important; } }

      .gv-inline-head { margin-bottom: 10px; flex: 0 0 auto; }
      .gv-inline-title { font-weight: 700; font-size: 14px; color: #0f172a; }
      .gv-inline-hint { font-size: 12px; color: #64748b; margin-top: 2px; }

      .gv-inline-answer {
        margin-top: 10px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 10px;
        flex: 1 1 auto;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }
      .gv-inline-loading-text { font-size: 13px; color: #334155; }
      .gv-inline-error { font-size: 13px; color: #b91c1c; }
      .gv-inline-answer-text { font-size: 13px; color: #0f172a; line-height: 1.55; }

      .gv-inline-inputbox {
        display: flex;
        gap: 8px;
        align-items: flex-end;
        margin-top: 10px;
        position: sticky;
        bottom: 0;
        background: #fff;
        padding-top: 8px;
        flex: 0 0 auto;
      }
      .gv-inline-inp {
        flex: 1;
        border: 1px solid #d1d5db;
        border-radius: 999px;
        padding: 12px 14px;
        resize: none;
        font-family: var(--gv-font);
        font-size: 13px;
        outline: none;
        min-height: 44px;
      }
      .gv-inline-send {
        background: var(--gv-navy);
        color: white;
        border: none;
        border-radius: 999px;
        padding: 12px 16px;
        font-weight: 800;
        font-size: 13px;
        cursor: pointer;
        min-height: 44px;
        flex: 0 0 auto;
      }

      .gv-policy-links {
        margin-top: 12px;
        background: #fff;
        border: 1px solid var(--gv-gray-200);
        border-radius: 12px;
        padding: 10px;
      }
      .gv-policy-title { font-weight: 800; font-size: 12px; margin-bottom: 6px; color: #0f172a; text-transform: uppercase; letter-spacing: .4px; }
      .gv-policy-link { display:block; color:#0f172a; text-decoration: underline; font-weight: 700; font-size: 13px; margin: 6px 0; }

      /* Cards */
      .gv-prod-cards-wrap { margin-top: 12px; padding-top: 10px; border-top: 1px dashed #e2e8f0; }
      .gv-prod-cards-title { font-weight: 900; font-size: 12px; color: #0f172a; margin-bottom: 8px; letter-spacing: 0.2px; text-transform: uppercase; }
      .gv-prod-cards { display: flex; flex-direction: column; gap: 10px; }
      .gv-prod-card { display: flex; gap: 10px; border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px; background: #fff; }
      .gv-prod-img { width: 64px; height: 64px; object-fit: contain; border-radius: 10px; border: 1px solid #eef2f7; background: white; }
      .gv-prod-meta { flex: 1; min-width: 0; }
      .gv-prod-name { font-size: 13px; font-weight: 800; color: #0f172a; line-height: 1.2; margin-bottom: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .gv-prod-variant { font-size: 12px; color: #475569; margin-bottom: 6px; }
      .gv-prod-price { font-size: 12px; color: #334155; margin-bottom: 8px; }
      .gv-prod-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
      .gv-prod-link { font-size: 12px; font-weight: 800; color: #0f172a; text-decoration: underline; }
      .gv-prod-add { padding: 10px 12px; font-size: 12px; border-radius: 999px; }

      .gv-purchase-card { margin-top: 12px; display: flex; gap: 10px; border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px; background: #fff; }
      .gv-purchase-img { width: 64px; height: 64px; object-fit: contain; border-radius: 10px; border: 1px solid #eef2f7; }
      .gv-purchase-meta { flex: 1; min-width: 0; }
      .gv-purchase-title { font-size: 13px; font-weight: 900; color: #0f172a; margin-bottom: 4px; overflow:hidden; text-overflow: ellipsis; white-space: nowrap; }
      .gv-purchase-variant { font-size: 12px; color: #475569; margin-bottom: 6px; }
      .gv-purchase-price { font-size: 12px; color: #334155; margin-bottom: 10px; }
      .gv-purchase-actions { display:flex; gap:10px; flex-wrap: wrap; }

      .gv-btn-add-to-cart,
      .gv-btn-buy-now {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: var(--gv-navy);
        color: white !important;
        padding: 10px 14px;
        border-radius: 999px;
        font-weight: 900;
        font-size: 12px;
        cursor: pointer;
        border: none;
        line-height: 1;
      }
      .gv-btn-buy-now { background: #111827; }
      .gv-btn-add-to-cart.gv-added { background: #10b981 !important; }

      .gv-spin { display: inline-block; animation: gv-spin 1s linear infinite; margin-right: 6px; }
      @keyframes gv-spin { 100% { transform: rotate(360deg); } }

      /* Sidebar */
      .gv-sidebar {
        position: fixed;
        bottom: 20px;
        right: 20px;
        width: var(--gv-panel-w);
        height: 600px;
        max-height: 80vh;
        background: white;
        z-index: 2147483647;
        box-shadow: 0 10px 40px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05);
        display: none;
        flex-direction: column;
        border-radius: 16px;
        overflow: hidden;
        isolation: isolate;
      }
      .gv-sidebar.active { display: flex; }
      .gv-sidebar-overlay {
        position: fixed; top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 2147483646;
        opacity: 0; visibility: hidden;
        transition: opacity 0.3s, visibility 0.3s;
        pointer-events: none;
      }
      .gv-sidebar-overlay.active { opacity: 1; visibility: visible; pointer-events: auto; cursor: pointer; }

      .gv-header { background: var(--gv-bg-dark); color: white; padding: 16px 20px; display: flex; align-items: center; justify-content: space-between; cursor: grab; user-select: none; }
      .gv-header:active { cursor: grabbing; }
      .gv-brand { display: flex; align-items: center; gap: 12px; pointer-events: none; }
      .gv-logo-img { height: 32px; width: auto; object-fit: contain; }
      .gv-brand-name { font-weight: 800; font-size: 16px; display: flex; align-items: center; gap: 6px; }
      .gv-beta-tag { font-size: 9px; background: rgba(255,255,255,0.2); padding: 1px 4px; border-radius: 3px; font-weight: 700; }

      .gv-header-controls { display: flex; align-items: center; gap: 8px; }
      .gv-minimize-icon, .gv-close-icon {
        cursor: pointer; padding: 6px; display: flex; align-items: center; justify-content: center;
        opacity: 0.85; transition: opacity 0.2s, background-color 0.2s;
        border-radius: 50%; width: 32px; height: 32px;
      }
      .gv-minimize-icon:hover, .gv-close-icon:hover { opacity: 1; background: rgba(255,255,255,0.1); }

      .gv-chat-body { flex: 1; background: var(--gv-gray-50); padding: 20px; overflow-y: auto; display: flex; flex-direction: column; }
      .gv-welcome-card { text-align: center; padding: 20px; color: var(--gv-gray-800); background: white; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
      .gv-row { display: flex; gap: 10px; margin-bottom: 16px; width: 100%; }
      .gv-row-user { justify-content: flex-end; }
      .gv-row-ai { justify-content: flex-start; }
      .gv-avatar-ai { width: 28px; height: 28px; background: var(--gv-bg-dark); color: var(--gv-accent); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0; margin-top: 2px; }
      .gv-bubble {
        max-width: 85%;
        padding: 12px 16px;
        border-radius: 12px;
        font-size: 14px;
        line-height: 1.55;
        border: 1px solid var(--gv-gray-200);
        background: white;
        color: var(--gv-gray-800);
      }
      .gv-row-user .gv-bubble { background: var(--gv-bg-dark); color: white; border: none; }
      .gv-bubble-extra { margin-top: 10px; width: 100%; }

      /* Compact cards inside bubble */
      .gv-bubble .gv-prod-card,
      .gv-bubble .gv-purchase-card {
        padding: 9px;
        border-radius: 12px;
      }
      .gv-bubble .gv-prod-img,
      .gv-bubble .gv-purchase-img { width: 56px; height: 56px; border-radius: 10px; }
      .gv-bubble .gv-prod-name,
      .gv-bubble .gv-purchase-title { font-size: 13px; }
      .gv-bubble .gv-btn-add-to-cart,
      .gv-bubble .gv-btn-buy-now { padding: 9px 12px; font-size: 12px; }

      .gv-footer { padding: 16px; background: white; border-top: 1px solid var(--gv-gray-200); }
      .gv-input-box { display: flex; gap: 8px; align-items: flex-end; background: white; border: 1px solid #d1d5db; border-radius: 999px; padding: 6px 6px 6px 16px; }
      .gv-sidebar-inp { flex: 1; border: none; outline: none; resize: none; max-height: 100px; padding: 8px 0; font-family: inherit; font-size: 14px; background: transparent; }
      .gv-send-btn { width: 40px; height: 40px; border-radius: 999px; background: var(--gv-bg-dark); color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; }
      .gv-disclaimer { text-align: center; font-size: 10px; color: #9ca3af; margin-top: 8px; }

      .gv-bubble-typing .gv-dot {
        display: inline-block;
        width: 6px; height: 6px;
        background: #9ca3af;
        border-radius: 50%;
        margin: 0 2px;
        animation: gv-bounce 1.4s infinite;
      }
      .gv-bubble-typing .gv-dot:nth-child(2) { animation-delay: 0.2s; }
      .gv-bubble-typing .gv-dot:nth-child(3) { animation-delay: 0.4s; }
      @keyframes gv-bounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-6px); } }

      .gv-followup-pills { display:flex; gap:8px; flex-wrap:wrap; margin-top:10px; }
      .gv-followup-pill { background:#fff; }

      .gv-loading-state .gv-skel {
        height: 32px;
        width: 80px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        border-radius: 16px;
        animation: gv-skeleton-loading 1.5s infinite;
        background-size: 200% 100%;
      }
      .gv-loading-state .gv-skel:nth-child(2) { width: 120px; }
      .gv-loading-state .gv-skel:nth-child(3) { width: 90px; }
      @keyframes gv-skeleton-loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      .gv-minimized-balloon {
        position: fixed;
        bottom: 18px;
        right: 18px;
        z-index: 2147483647;
        display: none;
        cursor: pointer;
      }
      .gv-minimized-balloon.active { display: block; }
      .gv-minimized-content {
        display:flex; align-items:center; gap:10px;
        background: #0a101d;
        color:#fff;
        padding: 10px 12px;
        border-radius: 999px;
        box-shadow: 0 10px 30px rgba(0,0,0,.25);
        border: 1px solid rgba(255,255,255,.12);
      }
      .gv-minimized-avatar { width:28px; height:28px; border-radius:50%; background:#0a192f; display:flex; align-items:center; justify-content:center; color: var(--gv-accent); }
      .gv-minimized-title { font-weight: 800; font-size: 12px; }
      .gv-minimized-badge {
        margin-left: 8px;
        width: 18px; height: 18px;
        border-radius: 999px;
        background: var(--gv-accent);
        color: #0a101d;
        display:none;
        align-items:center;
        justify-content:center;
        font-weight: 900;
        font-size: 11px;
      }
    `;
    document.head.appendChild(styles);
  }


  function makeDraggable(element, handle) {
    let isDragging = false;
    let startX, startY, initialLeft, initialTop;
    const HEADER_LIMIT = 80;

    handle.addEventListener('mousedown', dragStart);
    handle.addEventListener('touchstart', dragStart, { passive: false });

    function dragStart(e) {
      if (e.target.closest('.gv-close-icon') || e.target.closest('.gv-minimize-icon')) return;
      if (e.type === 'mousedown' && e.button !== 0) return;

      isDragging = true;
      const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;

      const rect = element.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;
      startX = clientX;
      startY = clientY;

      element.style.transition = 'none';
      element.style.bottom = 'auto';
      element.style.right = 'auto';
      element.style.left = `${initialLeft}px`;
      element.style.top = `${initialTop}px`;

      document.addEventListener('mousemove', drag);
      document.addEventListener('touchmove', drag, { passive: false });
      document.addEventListener('mouseup', dragEnd);
      document.addEventListener('touchend', dragEnd);
    }

    function drag(e) {
      if (!isDragging) return;
      e.preventDefault();

      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;

      const deltaX = clientX - startX;
      const deltaY = clientY - startY;

      let newLeft = initialLeft + deltaX;
      let newTop = initialTop + deltaY;

      if (newTop < HEADER_LIMIT) newTop = HEADER_LIMIT;
      const maxTop = window.innerHeight - element.offsetHeight - 10;
      if (newTop > maxTop) newTop = maxTop;

      const maxLeft = window.innerWidth - element.offsetWidth;
      if (newLeft < 0) newLeft = 0;
      if (newLeft > maxLeft) newLeft = maxLeft;

      element.style.left = `${newLeft}px`;
      element.style.top = `${newTop}px`;
    }


    function dragEnd() {
      if (!isDragging) return;
      isDragging = false;

      setTimeout(() => { element.style.transition = ''; }, 0);


      document.removeEventListener('mousemove', drag);
      document.removeEventListener('touchmove', drag);
      document.removeEventListener('mouseup', dragEnd);
      document.removeEventListener('touchend', dragEnd);
    }
  }



  function scrollToBottom(scrollEl) {
    if (!scrollEl) return;
    scrollEl.scrollTop = scrollEl.scrollHeight;
  }



})();